import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { useLeaveActions, useLeaveAllocations } from '../hooks';
import { LeaveRequestForm, LeaveRequestFormValues } from './LeaveRequestForm';

export interface SelfLeaveRequestDialogProps {
	open: boolean;
	onClose: () => void;
	staffName: string;
	userUid: string;
	onSubmitSuccess?: (requestId: string) => void;
}

/**
 * A focused, reusable “Request leave” dialog for self-submission.
 *
 * Why this exists:
 * - For `USER` and `MANAGER`, the default page should be request history.
 * - A single primary CTA (“Request leave”) should open this dialog, instead of a dedicated tab.
 *
 * UX notes:
 * - We rely on `useLeaveActions.submitLeaveRequest()` for global success/error snackbars.
 * - We additionally show an inline error banner when the submit returns `null` so the user
 *   has local context and an obvious retry path without hunting for toasts.
 * - Allocation subscription is only active while the dialog is open.
 */
export const SelfLeaveRequestDialog: React.FC<SelfLeaveRequestDialogProps> = ({
	open,
	onClose,
	staffName,
	userUid,
	onSubmitSuccess,
}) => {
	const { submitLeaveRequest } = useLeaveActions();
	const [submitting, setSubmitting] = useState(false);
	const [inlineError, setInlineError] = useState<string | null>(null);

	// Allocate subscription only while open to avoid background reads when the dialog is not visible.
	const allocationFilter = useMemo(() => {
		if (!open || !userUid) return null;
		return {
			role: 'USER' as const,
			uid: userUid,
			year: new Date().getFullYear(),
		};
	}, [open, userUid]);

	const { data: allocations, loading: allocationsLoading, error: allocationsError } =
		useLeaveAllocations(allocationFilter);

	const allocationSummary = useMemo(() => {
		const userAllocation = allocations.length > 0 ? allocations[0] : null;
		if (!userAllocation) return undefined;
		return {
			allowanceDays: userAllocation.allowanceDays,
			approvedDays: userAllocation.approvedDays,
			pendingDays: userAllocation.pendingDays,
			deniedDays: userAllocation.deniedDays,
		};
	}, [allocations]);

	useEffect(() => {
		if (!open) return;
		console.info('[leave-ui] open self leave request dialog', { userUid });
	}, [open, userUid]);

	useEffect(() => {
		// Keep the dialog deterministic between opens.
		if (!open) {
			setSubmitting(false);
			setInlineError(null);
		}
	}, [open]);

	const handleSubmit = async (values: LeaveRequestFormValues) => {
		// Defensive guard: the form already enforces dates, but we do not assume it will always do so.
		if (!userUid) {
			console.error('[leave-ui] cannot submit leave request: missing userUid');
			setInlineError('Unable to submit right now. Please refresh and try again.');
			return;
		}
		if (!values.startDate || !values.endDate) {
			console.warn('[leave-ui] leave request submit blocked: missing dates', { userUid });
			setInlineError('Select both a start date and end date.');
			return;
		}

		setSubmitting(true);
		setInlineError(null);

		console.info('[leave-ui] submitting leave request', {
			userUid,
			startDate: values.startDate.toISOString(),
			endDate: values.endDate.toISOString(),
			reason: values.reason,
		});

		try {
			const requestId = await submitLeaveRequest({
				startDate: values.startDate,
				endDate: values.endDate,
				reason: values.reason,
				comment: values.comment,
			});

			if (!requestId) {
				console.error('[leave-ui] leave request submit failed (null requestId)', { userUid });
				setInlineError('Unable to submit right now. Please try again.');
				return;
			}

			console.info('[leave-ui] leave request submitted', { userUid, requestId });
			onSubmitSuccess?.(requestId);
			onClose();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>Request leave</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5}>
					<Paper
						elevation={0}
						sx={{
							p: 2.5,
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 2,
							background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, rgba(25, 118, 210, 0.02) 100%)',
						}}
					>
						<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
							<Box
								sx={{
									width: 44,
									height: 44,
									borderRadius: 2,
									backgroundColor: 'primary.main',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0,
								}}
							>
								<EditCalendarIcon sx={{ color: 'white', fontSize: 24 }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="subtitle1" fontWeight={700}>
									Submit a leave request
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Choose your dates and provide a short reason. You’ll be notified when it’s reviewed.
								</Typography>
							</Box>
						</Stack>

						<Divider sx={{ my: 2 }} />

						<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
							<Typography variant="body2" color="text.secondary">
								Requesting as:
							</Typography>
							<Chip label={staffName} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
						</Stack>
					</Paper>

					{allocationsLoading && <LinearProgress />}

					{allocationsError && (
						<Alert severity="warning">
							Unable to load your current allocation right now. You can still submit a request.
						</Alert>
					)}

					{inlineError && (
						<Alert severity="error" onClose={() => setInlineError(null)}>
							{inlineError}
						</Alert>
					)}

					{/* The form owns field-level validation; this dialog owns submission state + overall feedback. */}
					<LeaveRequestForm
						onSubmit={handleSubmit}
						submitLabel={submitting ? 'Submitting…' : 'Submit request'}
						helperNotice="Submitting sends your request to your manager for approval."
						allocationSummary={allocationSummary}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
